import { Link } from "@tanstack/react-router";
import type { CategorySlice } from "@finances/shared";
import styles from "./SliceTransactions.module.css";
import { focusFilter } from "./focus.js";
import { RequestFailed } from "../../lib/api.js";
import { formatDay, pluralise } from "../../lib/format.js";
import { toTransactionsSearch, type Scope } from "../../lib/lastMonth.js";
import { useTransactionPages } from "../../lib/queries.js";
import { useSession } from "../../lib/session.js";
import { Button, ErrorState, Money, Row, Skeleton, Stack, Text } from "../../ui/index.js";

const PAGE = 20;

/**
 * The rows behind one bar of the breakdown.
 *
 * Mounted only while its bar is open, which is what makes opening a slice the
 * thing that fetches it. What it asks for is the chart's own definition of that
 * slice rather than something close to it: a list that disagrees with the figure
 * somebody clicked to see it is worse than no list.
 */
export function SliceTransactions({
  slice,
  scope,
  accountIds,
}: {
  slice: CategorySlice;
  scope: Scope;
  accountIds: string[];
}) {
  const { householdId } = useSession();
  const search = sliceSearch(slice, scope);
  const query = useTransactionPages(householdId, {
    ...search,
    ...focusFilter(accountIds),
    limit: PAGE,
  });
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

  if (query.isPending) {
    return (
      <Stack gap={2} aria-busy="true">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} height="var(--space-5)" />
        ))}
      </Stack>
    );
  }

  if (query.error) {
    return (
      <ErrorState
        title="Could not load these transactions"
        body={query.error instanceof RequestFailed ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
      />
    );
  }

  // The bar is drawn from rows that existed a moment ago, so an empty list here
  // means they have moved rather than that nothing was ever there.
  if (rows.length === 0) {
    return (
      <Stack gap={2}>
        <Text size="sm" tone="muted">
          Nothing is filed here any more. It may have been recategorised since.
        </Text>
        <OpenInTransactions search={search} />
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      <ul className={styles.rows}>
        {rows.map((txn) => (
          <li key={txn.id} className={styles.row}>
            <span className={styles.details}>
              <span className={styles.description}>{txn.description}</span>
              <span className={styles.meta}>{formatDay(txn.date)}</span>
            </span>
            <Money pence={txn.amountPence} className={styles.amount} />
          </li>
        ))}
      </ul>

      <Row justify="between" align="center" gap={3} wrap>
        {/* What is on screen against the whole slice, so a part list never
            reads as the lot. */}
        <Text size="xs" tone="muted">
          {rows.length < slice.txnCount
            ? `${String(rows.length)} of ${pluralise(slice.txnCount, "transaction")}`
            : pluralise(slice.txnCount, "transaction")}
        </Text>
        <Row gap={2} align="center">
          {query.hasNextPage && (
            <Button
              size="sm"
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Load more
            </Button>
          )}
          <OpenInTransactions search={search} />
        </Row>
      </Row>
    </Stack>
  );
}

/** The same rows on the screen built for working through them. */
function OpenInTransactions({ search }: { search: ReturnType<typeof sliceSearch> }) {
  return (
    <Link to="/transactions" search={search} className={styles.quiet}>
      Open in Transactions
    </Link>
  );
}

/**
 * The slice, as a transactions filter — the same period and the same category
 * the bar was drawn from.
 *
 * The unfiled bar is the money-out half of what nobody has categorised: with no
 * category to go on, the chart splits those rows by their sign, so a refund
 * waiting to be filed is not in the bar and must not be in this list either.
 */
function sliceSearch(slice: CategorySlice, scope: Scope) {
  const period = toTransactionsSearch(scope);
  if (slice.categoryId) return { ...period, categoryId: slice.categoryId };
  return { ...period, uncategorised: true, direction: "out" as const };
}
